// =============================================================
// App shell — full app-frame parity with the hi-fi prototype (DesktopApp).
//
// Two nav layouts (PrefsContext.navMode):
//   • side  — collapsible icon-rail sidebar with brand, RBAC-filtered groups,
//             a collapse button, and a profile chip → profile popover.
//   • top   — top bar with brand, dropdown group menus, right-aligned profile.
// The side rail auto-collapses below 1080px (ResizeObserver); the effective
// collapsed state is `navCollapsed || narrow`. A profile drawer (My profile)
// shows the signed-in identity. Topbar keeps Ctrl/Cmd+K search, notifications,
// theme + language switches; sign-out moved into the profile menu.
// =============================================================

import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext.js";
import { usePrefs } from "./PrefsContext.js";
import { useSync } from "./SyncContext.js";
import { WEB_MODULES, type WebModule } from "./modules.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { useFeatureFlags } from "../lib/queries.js";
import { Icon, ICONS } from "../components/primitives.js";
import { CommandPalette } from "../components/CommandPalette.js";
import { NotificationCenter } from "../components/NotificationCenter.js";
import type { Lang } from "@perpetuum/core";

const LANG_LABEL: Record<Lang, string> = { lv: "LV", ru: "RU", en: "EN" };
/** Endonyms for the language menu — each language named in itself (no i18n). */
const LANG_NATIVE: Record<Lang, string> = { lv: "Latviešu", ru: "Русский", en: "English" };
const NAV_GROUPS = ["OVERVIEW", "PLANNING", "ADMIN"] as const;
const NARROW_PX = 1080;

/** Initials from a display name ("I. Liepa" → "IL", "super-admin" → "SU"). */
function initials(name: string): string {
  const parts = name.trim().split(/[\s.\-_]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0] ?? "";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? "";
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase();
}

export function Shell() {
  const { user, logout, seesTab, can } = useAuth();
  const { theme, toggleTheme, lang, setLang, langs, navMode, navCollapsed, setNavCollapsed } = usePrefs();
  const { online, pendingCount } = useSync();
  const t = useScopedT();
  const location = useLocation();
  const navigate = useNavigate();

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false); // profile popover
  const [profileDrawer, setProfileDrawer] = useState(false); // My-profile drawer
  const [openMenu, setOpenMenu] = useState<(typeof NAV_GROUPS)[number] | null>(null); // top-mode group dropdown
  const [langOpen, setLangOpen] = useState(false); // topbar language dropdown
  const [narrow, setNarrow] = useState(false);
  const [tip, setTip] = useState<{ text: string; top: number; left: number } | null>(null);
  const [navOverflow, setNavOverflow] = useState(false); // side-rail nav scroll-more hint

  const appRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  // Auto-collapse the rail below NARROW_PX (ResizeObserver on the app frame).
  useEffect(() => {
    const el = appRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setNarrow(entry.contentRect.width < NARROW_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const collapsed = navMode === "side" && (navCollapsed || narrow);

  // Global Ctrl/Cmd+K → command palette; Escape closes popover / drawer / menu.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (e.key === "Escape") {
        setProfileOpen(false);
        setProfileDrawer(false);
        setOpenMenu(null);
        setLangOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Per-tenant feature flags (Phase 9.1 toggles). A module carrying a
  // `featureFlag` is hidden when the tenant has that flag explicitly OFF; an
  // unknown/loading flag is treated as enabled (matches the server default-on).
  const { data: flags } = useFeatureFlags();
  const flagOff = useMemo(() => {
    const off = new Set<string>();
    for (const f of flags ?? []) if (!f.enabled) off.add(f.id);
    return off;
  }, [flags]);

  // RBAC- + feature-flag-filtered modules, grouped.
  const visible = useMemo(
    () => WEB_MODULES.filter((m) => seesTab(m.perm) && !(m.featureFlag && flagOff.has(m.featureFlag))),
    [seesTab, flagOff],
  );
  const groupItems = (g: (typeof NAV_GROUPS)[number]) =>
    visible.filter((m) => m.manifest.nav?.[0]?.group === g);

  // Side-rail nav-fade: show the bottom gradient + chevron only when the nav
  // overflows AND there is still content below the current scroll position
  // (hidden once scrolled to the bottom, and only in expanded side mode).
  // Re-measured on scroll, resize, and whenever the visible-module set changes.
  useEffect(() => {
    const el = navRef.current;
    if (!el || navMode !== "side" || collapsed) {
      setNavOverflow(false);
      return;
    }
    const measure = () => {
      setNavOverflow(el.scrollHeight - el.clientHeight - el.scrollTop > 4);
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      el.removeEventListener("scroll", measure);
      ro?.disconnect();
    };
  }, [navMode, collapsed, visible.length]);

  // Client's resolved IANA timezone (e.g. "Europe/Riga") — genuinely available
  // via Intl, shown as a static label. NOT a live clock; empty string when the
  // runtime can't resolve a zone (then the label is omitted rather than faked).
  const timezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
    } catch {
      return "";
    }
  }, []);

  // Iterum brand symbol (prototype DesktopApp .d-brand): theme-aware PNG from
  // /public — ondark for the dark theme, multicolor for light. Decorative
  // (alt="") — the wordmark next to it carries the name.
  const brandSymbol = theme === "dark" ? "/iterum-symbol-ondark.png" : "/iterum-symbol-multicolor.png";

  const displayName = user?.name ?? "—";
  const avatarText = user ? initials(user.name) : "?";
  const role = user?.groupName;
  // "username · companyName" — omit pieces we don't have rather than faking.
  const profileSub = [user?.username, user?.companyName].filter(Boolean).join(" · ");

  // Rail tooltip handlers (collapsed side rail only).
  const tipOn = (label: string) =>
    collapsed
      ? (e: React.MouseEvent<HTMLElement>) => {
          const r = e.currentTarget.getBoundingClientRect();
          setTip({ text: label, top: r.top + r.height / 2, left: r.right + 10 });
        }
      : undefined;
  const tipOff = collapsed ? () => setTip(null) : undefined;

  const closeOverlays = () => {
    setProfileOpen(false);
    setOpenMenu(null);
    setLangOpen(false);
  };

  const onSignOut = () => {
    setProfileOpen(false);
    void logout();
  };

  // ---- shared profile-popover body ----
  const ProfilePopover = ({ variant }: { variant: "side" | "top" }) => (
    <div className={`chrome-pop profile-pop ${variant}`} role="menu">
      <div className="prof-hd">
        <div className="d-avatar lg">{avatarText}</div>
        <div>
          <div className="prof-name">{displayName}</div>
          {profileSub && <div className="prof-sub">{profileSub}</div>}
        </div>
      </div>
      <button
        className="prof-item"
        role="menuitem"
        onClick={() => {
          setProfileDrawer(true);
          setProfileOpen(false);
        }}
      >
        <Icon d={ICONS.user} size={17} />
        {t("profile.myProfile")}
      </button>
      <button
        className="prof-item"
        role="menuitem"
        onClick={() => {
          setProfileOpen(false);
          navigate("/settings");
        }}
      >
        <Icon d={ICONS.cog} size={17} />
        {t("profile.preferences")}
      </button>
      <button
        className="prof-item"
        role="menuitem"
        onClick={() => {
          toggleTheme();
          setProfileOpen(false);
        }}
      >
        <Icon d={theme === "dark" ? ICONS.sun : ICONS.moon} size={17} />
        {t("profile.toggleTheme")}
      </button>
      <div className="prof-div" />
      <button className="prof-item danger" role="menuitem" onClick={onSignOut}>
        <Icon d={ICONS.logout} size={17} />
        {t("common.signOut")}
      </button>
    </div>
  );

  const railNavLink = (m: WebModule) => {
    const enabled = can(m.perm, "read");
    const label = t(m.labelKey);
    return (
      <NavLink
        key={m.path}
        to={m.path}
        className={({ isActive }) => `d-link ${isActive ? "active" : ""} ${enabled ? "" : "disabled"}`}
        onClick={(e) => {
          if (!enabled) e.preventDefault();
        }}
        aria-disabled={!enabled}
        aria-label={label}
        onMouseEnter={tipOn(label)}
        onMouseLeave={tipOff}
      >
        <Icon d={(ICONS as Record<string, string>)[m.icon] ?? ICONS.box} size={19} />
        <span className="d-link-label">{label}</span>
      </NavLink>
    );
  };

  return (
    <div ref={appRef} className={`d-app ${navMode === "top" ? "nav-top" : collapsed ? "nav-collapsed" : ""}`}>
      {/* ---- SIDE MODE: sidebar ---- */}
      {navMode === "side" && (
        <aside className="d-side">
          <div className="d-brand">
            <img src={brandSymbol} alt="" />
            <span className="nm">
              {t("app.name").toLowerCase()}
              <span className="dot">.</span>
            </span>
          </div>
          <div className="d-nav-wrap">
            <nav className="d-nav" aria-label="Primary" ref={navRef}>
              {NAV_GROUPS.map((g) => {
                const items = groupItems(g);
                if (items.length === 0) return null;
                return (
                  <div key={g}>
                    <div className="grp">{t(`nav.${g.toLowerCase()}`)}</div>
                    {items.map(railNavLink)}
                  </div>
                );
              })}
            </nav>
            {!collapsed && (
              <div className={`d-nav-fade ${navOverflow ? "show" : ""}`} aria-hidden="true">
                <Icon d={ICONS.chevronDown} size={18} />
              </div>
            )}
          </div>
          <button
            className="d-collapse-btn"
            onClick={() => setNavCollapsed(!navCollapsed)}
            aria-label={navCollapsed ? t("nav.expand") : t("nav.collapse")}
            onMouseEnter={tipOn(narrow ? t("nav.autoCollapsed") : t("nav.expand"))}
            onMouseLeave={tipOff}
          >
            <Icon d={collapsed ? ICONS.expand : ICONS.collapse} size={18} />
            <span className="d-link-label">{t("nav.collapse")}</span>
          </button>
          <button
            className="d-side-foot as-btn"
            onClick={() => setProfileOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={profileOpen}
            onMouseEnter={tipOn(`${displayName} · ${t("profile.myProfile")}`)}
            onMouseLeave={tipOff}
          >
            <div className="d-avatar">{avatarText}</div>
            <div className="who">
              {displayName}
              {role && <small>{role}</small>}
            </div>
            <span className="who-chev">
              <Icon d={ICONS.chevronUp} size={15} />
            </span>
          </button>
          {profileOpen && <ProfilePopover variant="side" />}
        </aside>
      )}
      {tip && navMode === "side" && collapsed && (
        <div className="rail-tip" style={{ top: tip.top, left: tip.left }}>
          {tip.text}
        </div>
      )}

      {/* ---- MAIN COLUMN ---- */}
      <main className="d-main">
        {navMode === "top" && (
          <div className="d-topnav">
            <div className="d-brand tn">
              <img src={brandSymbol} alt="" />
              <span className="nm">
                {t("app.name").toLowerCase()}
                <span className="dot">.</span>
              </span>
            </div>
            <nav className="tn-groups" aria-label="Primary">
              {NAV_GROUPS.map((g) => {
                const items = groupItems(g);
                if (items.length === 0) return null;
                const active = items.some((m) => location.pathname.startsWith(m.path));
                return (
                  <div className="tn-group" key={g}>
                    <button
                      className={`tn-gbtn ${active ? "active" : ""} ${openMenu === g ? "open" : ""}`}
                      aria-haspopup="menu"
                      aria-expanded={openMenu === g}
                      onClick={() => setOpenMenu((m) => (m === g ? null : g))}
                    >
                      {t(`nav.${g.toLowerCase()}`)}
                      <Icon d={ICONS.chevronDown} size={14} />
                    </button>
                    {openMenu === g && (
                      <div className="tn-menu" role="menu">
                        {items.map((m) => {
                          const enabled = can(m.perm, "read");
                          return (
                            <NavLink
                              key={m.path}
                              to={m.path}
                              role="menuitem"
                              className={({ isActive }) =>
                                `tn-item ${isActive ? "active" : ""} ${enabled ? "" : "disabled"}`
                              }
                              onClick={(e) => {
                                if (!enabled) {
                                  e.preventDefault();
                                  return;
                                }
                                setOpenMenu(null);
                              }}
                              aria-disabled={!enabled}
                            >
                              <Icon d={(ICONS as Record<string, string>)[m.icon] ?? ICONS.box} size={17} />
                              <span>{t(m.labelKey)}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
            <span style={{ flex: 1 }} />
            <button
              className="tn-foot as-btn"
              onClick={() => setProfileOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={profileOpen}
              aria-label={`${displayName} · ${t("profile.myProfile")}`}
            >
              <div className="d-avatar">{avatarText}</div>
            </button>
            {profileOpen && <ProfilePopover variant="top" />}
          </div>
        )}
        {openMenu && navMode === "top" && <div className="tn-scrim" onClick={() => setOpenMenu(null)} />}

        <div className="shell-topbar">
          <button
            className="cmdk-trigger"
            onClick={() => setPaletteOpen(true)}
            aria-label={t("chrome.search")}
            data-testid="cmdk-trigger"
          >
            <Icon d={ICONS.search} size={15} />
            <span className="cmdk-trigger-label">{t("chrome.search")}</span>
            <kbd className="cmdk-trigger-kbd">Ctrl K</kbd>
          </button>
          <span className="sp" />
          <NotificationCenter />
          <div className="seg" role="group" aria-label={t("common.theme")}>
            <button onClick={toggleTheme} aria-label={t("profile.toggleTheme")}>
              <Icon d={theme === "dark" ? ICONS.sun : ICONS.moon} size={16} />
            </button>
          </div>
          {/* One compact language control (was a 3-button LV/RU/EN seg):
              current code + globe/chevron → riseIn dropdown listing the
              languages by their endonyms. aria-haspopup="listbox" (NOT "menu"
              — the profile chip owns that role in the chrome). */}
          <div className="lang-wrap">
            <button
              className="lang-btn"
              onClick={() => setLangOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={langOpen}
              aria-label={t("profile.language")}
              data-testid="lang-trigger"
            >
              <Icon d={ICONS.globe} size={15} />
              {LANG_LABEL[lang]}
              <Icon d={ICONS.chevronDown} size={13} />
            </button>
            {langOpen && (
              <div className="lang-menu" role="listbox" aria-label={t("profile.language")}>
                {langs.map((l) => (
                  <button
                    key={l}
                    role="option"
                    aria-selected={lang === l}
                    className="lang-opt"
                    onClick={() => {
                      setLang(l);
                      setLangOpen(false);
                    }}
                  >
                    <span className="lang-name">{LANG_NATIVE[l]}</span>
                    <span className="lang-code mono">{LANG_LABEL[l]}</span>
                    {lang === l && <Icon d={ICONS.check} size={15} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="shell-main-outlet">
          <Outlet key={location.pathname} />
        </div>
        {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
      </main>

      {/* scrim closes the profile popover / language dropdown (matches prototype chrome-scrim) */}
      {(profileOpen || langOpen) && <div className="chrome-scrim" onClick={closeOverlays} />}

      {/* ---- status footer (spans full width in top mode) ----
          REAL signals only: live online/offline + pending-sync + signed-in name.
          The version label is the real product build string. The timezone is the
          client's resolved IANA zone (genuinely available via Intl) rendered as a
          static label — NOT a faked live clock. No changelog affordance: there is
          no real changelog content/route to surface, so it's omitted. */}
      <footer className="d-statusbar">
        <span className="sb-item" data-testid="conn-status">
          <span className={`sb-dot ${online ? "ok" : "off"}`} />
          {online ? t("status.online") : t("chrome.offline")}
        </span>
        {pendingCount > 0 && (
          <>
            <span className="sb-sep">·</span>
            <span className="sb-pending sb-item" data-testid="sb-pending">
              <Icon d={ICONS.upload} size={12} /> {pendingCount} {t("chrome.pending")}
            </span>
          </>
        )}
        <span className="sb-sep">·</span>
        <span className="sb-item">
          {t("common.signedInAs")}: <b>{displayName}</b>
        </span>
        <span className="sp" />
        {timezone && (
          <>
            <span className="sb-item sb-mute mono" data-testid="sb-timezone">
              {timezone}
            </span>
            <span className="sb-sep">·</span>
          </>
        )}
        <span className="sb-item sb-ver mono" data-testid="sb-version">
          {t("status.version")}
        </span>
      </footer>

      {/* ---- profile drawer (My profile) ---- */}
      {profileDrawer && (
        <div className="gd-scrim" onClick={() => setProfileDrawer(false)}>
          <aside className="gd-drawer profile-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={t("profile.myProfile")}>
            <div className="gd-head">
              <div className="gd-head-top">
                {role && <span className="prof-role-chip">{role}</span>}
                <span className="sp" style={{ flex: 1 }} />
                <button className="gd-close" onClick={() => setProfileDrawer(false)} aria-label={t("common.close")}>
                  <Icon d={ICONS.x} size={18} />
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 8 }}>
                <div className="d-avatar lg">{avatarText}</div>
                <div>
                  <h3 style={{ margin: 0, fontWeight: 800, fontSize: 21 }}>{displayName}</h3>
                  {profileSub && <div className="gd-sub">{profileSub}</div>}
                </div>
              </div>
            </div>
            <div className="gd-body">
              {role && (
                <div className="gd-kv">
                  <span className="k">{t("profile.group")}</span>
                  <span className="v">{role}</span>
                </div>
              )}
              {user?.companyName && (
                <div className="gd-kv">
                  <span className="k">{t("profile.company")}</span>
                  <span className="v">{user.companyName}</span>
                </div>
              )}
              {user?.username && (
                <div className="gd-kv">
                  <span className="k">{t("profile.username")}</span>
                  <span className="v mono">{user.username}</span>
                </div>
              )}
              {user?.email && (
                <div className="gd-kv" data-testid="profile-email">
                  <span className="k">{t("profile.email")}</span>
                  <span className="v mono">{user.email}</span>
                </div>
              )}
              <div className="gd-kv">
                <span className="k">{t("profile.language")}</span>
                <span className="v">{LANG_LABEL[lang]}</span>
              </div>
            </div>
            <div className="gd-foot">
              <button className="btn btn-ghost btn-sm" onClick={() => setProfileDrawer(false)}>
                {t("common.close")}
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
